import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { 
  ArrowLeft,
  Clock,
  Wifi,
  WifiOff,
  Calendar,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

const LawyerWorkingHours = () => {
  const navigate = useNavigate();
  const { language, user } = useApp();
  const [workingHours, setWorkingHours] = useState({
    totalActiveHours: 0,
    totalOfflineHours: 0,
    weeklySchedule: {}
  });

  const translations = {
    english: {
      workingHours: 'Working Hours',
      back: 'Back',
      totalActiveHours: 'Total Active Hours',
      totalOfflineHours: 'Total Offline Hours',
      weeklySchedule: 'Weekly Schedule',
      thisWeek: 'This Week',
      lastWeek: 'Last Week',
      thisMonth: 'This Month',
      active: 'Active',
      offline: 'Offline',
      hours: 'hours',
      minutes: 'minutes',
      noData: 'No working hours data available',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    },
    hindi: {
      workingHours: 'कार्य समय',
      back: 'वापस',
      totalActiveHours: 'कुल सक्रिय घंटे',
      totalOfflineHours: 'कुल ऑफलाइन घंटे',
      weeklySchedule: 'साप्ताहिक कार्यक्रम',
      thisWeek: 'इस सप्ताह',
      lastWeek: 'पिछला सप्ताह',
      thisMonth: 'इस महीने',
      active: 'सक्रिय',
      offline: 'ऑफलाइन',
      hours: 'घंटे',
      minutes: 'मिनट',
      noData: 'कोई कार्य समय डेटा उपलब्ध नहीं है',
      monday: 'सोमवार',
      tuesday: 'मंगलवार',
      wednesday: 'बुधवार',
      thursday: 'गुरुवार',
      friday: 'शुक्रवार',
      saturday: 'शनिवार',
      sunday: 'रविवार'
    }
  };

  const t = translations[language] || translations.english;

  // Load working hours data from localStorage
  useEffect(() => {
    try {
      const lawyerStatus = JSON.parse(localStorage.getItem('lawyer_status') || '{}');
      const lawyerScheduledOffline = JSON.parse(localStorage.getItem('lawyer_scheduled_offline') || '[]');
      
      // Calculate total active and offline hours
      let totalActive = 0;
      let totalOffline = 0;
      
      // For demo purposes, let's calculate some sample data
      const now = new Date();
      const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
      
      // Sample weekly schedule
      const weeklySchedule = {
        monday: { active: 8, offline: 16, isWorking: true },
        tuesday: { active: 7, offline: 17, isWorking: true },
        wednesday: { active: 6, offline: 18, isWorking: true },
        thursday: { active: 8, offline: 16, isWorking: true },
        friday: { active: 7, offline: 17, isWorking: true },
        saturday: { active: 4, offline: 20, isWorking: true },
        sunday: { active: 0, offline: 24, isWorking: false }
      };
      
      // Calculate totals
      Object.values(weeklySchedule).forEach(day => {
        if (day.isWorking) {
          totalActive += day.active;
          totalOffline += day.offline;
        }
      });
      
      setWorkingHours({
        totalActiveHours: totalActive,
        totalOfflineHours: totalOffline,
        weeklySchedule
      });
    } catch (error) {
      console.error('Error loading working hours:', error);
    }
  }, []);

  const formatHours = (hours) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours} ${t.hours} ${minutes} ${t.minutes}`;
  };

  const daysOfWeek = [
    { key: 'monday', label: t.monday },
    { key: 'tuesday', label: t.tuesday },
    { key: 'wednesday', label: t.wednesday },
    { key: 'thursday', label: t.thursday },
    { key: 'friday', label: t.friday },
    { key: 'saturday', label: t.saturday },
    { key: 'sunday', label: t.sunday }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-lavender-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/lawyer-settings')}
                className="w-10 h-10 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-purple-500" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t.workingHours}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Track your working hours and availability
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Total Hours Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"
        >
          {/* Total Active Hours */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <Wifi className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t.totalActiveHours}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t.thisWeek}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-green-500">
                {formatHours(workingHours.totalActiveHours)}
              </span>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
          </div>

          {/* Total Offline Hours */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <WifiOff className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t.totalOfflineHours}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t.thisWeek}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-gray-500">
                {formatHours(workingHours.totalOfflineHours)}
              </span>
              <TrendingDown className="w-5 h-5 text-gray-500" />
            </div>
          </div>
        </motion.div>

        {/* Weekly Schedule */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <div className="p-6 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-purple-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t.weeklySchedule}
              </h3>
            </div>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {daysOfWeek.map((day, index) => {
                const dayData = workingHours.weeklySchedule[day.key];
                const isWorking = dayData?.isWorking;
                
                return (
                  <div
                    key={day.key}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-all duration-200 ${
                      isWorking 
                        ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800' 
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        isWorking ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {day.label}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {isWorking ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Wifi className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-green-600 dark:text-green-400">
                              {dayData.active} {t.hours}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <WifiOff className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {dayData.offline} {t.hours}
                            </span>
                          </div>
                        </>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t.offline}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LawyerWorkingHours;
